import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { AiAskResponse, AiTable } from '@kassa/shared';
import { api, ApiError } from '../api';
import { PageHeader } from '../components/ListPage';

// Köməkçi (AI, KT-100): söhbət. Rəqəmlər alətdən gəlir və cədvəl kimi göstərilir; model yalnız qısa izah yazır.
// Söhbət yalnız bu ekranda yaşayır (yeniləyəndə sıfırlanır); serverdə qısa müddət Redis-də, loga yazılmır.

const SUGGESTIONS = ['Bu gün nə qədər satmışam?', 'Hansı mallar qurtarır?', 'Kim borcludur?', 'Bu ay ən çox nə satılıb?'];
const QUESTION_MAX = 1000;

interface Entry {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  tables: AiTable[];
  failed?: boolean;
}

export function Assistant({ initialQuestion }: { initialQuestion?: string }) {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [usage, setUsage] = useState<AiAskResponse['usage'] | null>(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);
  const endRef = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  const ask = async (question: string) => {
    const text = question.trim();
    if (!text || busy) return;
    setBusy(true);
    setQ('');
    setEntries((e) => [...e, { id: ++seq.current, role: 'user', text, tables: [] }]);
    try {
      const r = await api.aiAsk(text.slice(0, QUESTION_MAX), conversationId);
      setConversationId(r.conversationId);
      setUsage(r.usage);
      setEntries((e) => [...e, { id: ++seq.current, role: 'assistant', text: r.message, tables: r.tables, failed: !r.answered }]);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Şəbəkə xətası — yenidən yoxlayın';
      setEntries((e) => [...e, { id: ++seq.current, role: 'assistant', text: message, tables: [], failed: true }]);
    } finally {
      setBusy(false);
    }
  };

  // Ana səhifədən gələn sual bir dəfə göndərilir
  useEffect(() => {
    if (initialQuestion && !started.current) {
      started.current = true;
      void ask(initialQuestion);
    }
  }, [initialQuestion]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [entries.length]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void ask(q);
  };

  const reset = () => {
    setEntries([]);
    setConversationId(null);
  };

  return (
    <main className="page">
      <PageHeader
        title="Köməkçi (AI)"
        sub={usage ? `Bu ay: ${fmt(usage.usedAzn)} / ${fmt(usage.limitAzn)} ₼` : undefined}
        actions={
          entries.length > 0 ? (
            <button type="button" className="btn-small" onClick={reset} disabled={busy}>
              Yeni söhbət
            </button>
          ) : undefined
        }
      />
      {usage?.warning && (
        <p className="ai-note" role="status">
          AI limitinin 80%-dən çoxu işlənib. Limit dolanda yalnız köməkçi dayanır — kassa işləməyə davam edir.
        </p>
      )}

      <section className="card ai-chat" aria-label="Söhbət" aria-live="polite" aria-busy={busy}>
        {entries.length === 0 && (
          <div className="ai-empty">
            <p className="muted">Satış, qalıq, borc və paket barədə soruşun. Köməkçi yalnız oxuyur — heç nəyi dəyişmir.</p>
            <div className="ai-suggest">
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" className="btn-small" onClick={() => void ask(s)} disabled={busy}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {entries.map((e) => (
          <div key={e.id} className={`ai-msg ai-${e.role}${e.failed ? ' ai-failed' : ''}`}>
            <p className="ai-text">{e.text}</p>
            {e.tables.map((t, i) => (
              <AiTableView key={i} table={t} />
            ))}
          </div>
        ))}
        {busy && (
          <div className="ai-msg ai-assistant">
            <p className="ai-text muted">Hazırlanır…</p>
          </div>
        )}
        <div ref={endRef} />
      </section>

      <form className="ai-form" onSubmit={submit}>
        <label className="sr-only" htmlFor="ai-q">
          Sual
        </label>
        <textarea
          id="ai-q"
          rows={2}
          maxLength={QUESTION_MAX}
          placeholder="Sualınızı yazın…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void ask(q);
            }
          }}
        />
        <button type="submit" className="btn-primary" disabled={busy || !q.trim()}>
          Göndər
        </button>
      </form>
    </main>
  );
}

function AiTableView({ table }: { table: AiTable }) {
  if (table.rows.length === 0) {
    return <p className="muted small">{table.title}: məlumat yoxdur.</p>;
  }
  return (
    <div className="ai-table-wrap">
      <table className="report-table" tabIndex={0} aria-label={table.title}>
        <caption>{table.title}</caption>
        <thead>
          <tr>
            {table.columns.map((c, i) => (
              <th key={i} className={isNum(table.rows[0]?.[i]) ? 'num' : undefined}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((r, i) => (
            <tr key={i}>
              {r.map((cell, j) => (
                <td key={j} className={isNum(cell) ? 'num' : undefined}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const NUM = /^[−-]?\d[\d\s]*(,\d+)?( ₼)?( \(\d+\))?$/;

function isNum(cell: string | undefined): boolean {
  return cell !== undefined && NUM.test(cell);
}

/** «4.37» → «4,37» (göstərmə; hesab serverdədir). */
function fmt(azn: string): string {
  return azn.replace('.', ',');
}
